import clsx from "clsx";

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { type MetricConfig } from "./metric-category-builder";

interface ReorderPanelProps<T extends MetricConfig> {
  reorderVisibleMetrics: (ordering: T[]) => void;
  visibleMetrics: T[];
}

export const ReorderPanel = <T extends MetricConfig>({
  reorderVisibleMetrics,
  visibleMetrics,
}: ReorderPanelProps<T>) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = visibleMetrics.findIndex(
        ({ name }) => name === active.id,
      );
      const newIndex = visibleMetrics.findIndex(
        ({ name }) => name === over?.id,
      );

      const newOrdering = arrayMove(visibleMetrics, oldIndex, newIndex);
      reorderVisibleMetrics(newOrdering);
    }
  };

  return (
    <div
      className="p-5 overflow-y-scroll space-y-2 overflow-y-auto px-7 pb-4 border-l"
      data-testid="metric-reorder-panel"
    >
      <h3 className="text-base font-semibold m-0 mb-3">
        <span>
          Metrics
          <span className="ml-1.5 text-xs font-light">
            ({visibleMetrics.length} selected)
          </span>
        </span>
      </h3>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={visibleMetrics.map(({ name }) => name)}>
          {visibleMetrics.map(({ name, visible }) => (
            <div key={name} className={clsx(!visible && "hidden")}>
              <ColumnOrderRow columnName={name} />
            </div>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

const ColumnOrderRow = ({ columnName }: { columnName: string }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: columnName,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "border rounded-md border-blue-300 bg-cosmic-4 hover:bg-cosmic-3",
        "hover:border-cosmic-3 text-xs font-light px-2 py-1.5",
        "flex items-center",
        "animate__animated animate__faster animate__fadeIn",
      )}
      style={style}
      {...attributes}
      {...listeners}
    >
      <i className="fa-regular fa-sharp fa-grip-dots text-blue-500 cursor-move mr-2" />
      <span className="flex-1">{columnName}</span>
    </div>
  );
};
